import type { MetricsSample } from './types';

export const MAX_DISPLAY_SAMPLES = 4_000;

// Keep chart work bounded while ensuring every pod remains represented. Each
// pod receives a proportional share of the display budget, including its
// first and latest sample whenever the share permits it.
export function downsampleMetricsForDisplay(
  samples: MetricsSample[],
  maxSamples = MAX_DISPLAY_SAMPLES,
): MetricsSample[] {
  if (samples.length <= maxSamples) return samples;
  if (maxSamples <= 0) return [];

  const byPod = new Map<string, MetricsSample[]>();
  for (const sample of samples) {
    const group = byPod.get(sample.pod);
    if (group) group.push(sample);
    else byPod.set(sample.pod, [sample]);
  }
  const groups = Array.from(byPod.values());
  for (const group of groups) group.sort((left, right) => left.ts_ms - right.ts_ms);

  if (groups.length >= maxSamples) {
    return evenlySelect(groups, maxSamples)
      .map((group) => group[group.length - 1])
      .sort((left, right) => left.ts_ms - right.ts_ms);
  }

  const quotas = groups.map(() => 1);
  let remaining = maxSamples - groups.length;
  const available = groups.reduce((total, group) => total + group.length - 1, 0);
  const remainders: Array<{ index: number; fraction: number }> = [];

  if (available > 0) {
    groups.forEach((group, index) => {
      const exact = remaining * (group.length - 1) / available;
      const additional = Math.min(group.length - 1, Math.floor(exact));
      quotas[index] += additional;
      remainders.push({ index, fraction: exact - additional });
    });
    remaining -= quotas.reduce((total, quota) => total + quota, 0) - groups.length;
  }

  remainders.sort((left, right) => right.fraction - left.fraction);
  while (remaining > 0) {
    let allocated = false;
    for (const { index } of remainders) {
      if (remaining === 0) break;
      if (quotas[index] >= groups[index].length) continue;
      quotas[index] += 1;
      remaining -= 1;
      allocated = true;
    }
    if (!allocated) break;
  }

  return groups
    .flatMap((group, index) => evenlySelect(group, quotas[index]))
    .sort((left, right) => left.ts_ms - right.ts_ms);
}

function evenlySelect<T>(values: T[], count: number): T[] {
  if (count >= values.length) return values;
  if (count <= 0) return [];
  if (count === 1) return [values[values.length - 1]];

  const lastIndex = values.length - 1;
  return Array.from({ length: count }, (_, index) => (
    values[Math.round(index * lastIndex / (count - 1))]
  ));
}
